import type { Thunk, Action } from 'easy-peasy';
import { thunk, action } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import type { Upload } from '../services/FileService/types';
import type { ApplicationErrorOptions } from '../../types';
import FileSaver from 'file-saver';
import type { UploadRequest } from '../services/FileService/types';
import parseStatusCode from '../errors/parseStatusCode';

interface RetrieveFileRequest {
  s3Location: string;
  fileName: string;
}
export interface FileModel {
  lastAction?: number;
  fileUploads: Upload[];
  fetchUploads: Thunk<FileModel, void, Injections, StoreModel, Promise<void>>;
  uploadError: Action<FileModel, ApplicationErrorOptions>;
  setUploads: Action<FileModel, Upload[]>;
  retrieveFile: Thunk<FileModel, RetrieveFileRequest, Injections, Promise<ArrayBuffer>>;
  fileActionStart: Action<FileModel, void>;
  uploadFile: Thunk<FileModel, UploadRequest, Injections, StoreModel, Promise<void>>;
  uploadSuccess: Action<FileModel, any>;
  deleteFile: Thunk<FileModel, string, Injections, Promise<string>>;
  removeFileFromlist: Action<FileModel, string>;
  deleteUploadError: Action<FileModel, ApplicationErrorOptions>;
  selectedDeleteFile?: string;
  setDeleteFile: Action<FileModel, string>;
}

export const fileModel: FileModel = {
  fileUploads: [],
  fileActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  uploadFile: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fileActionStart();
      const res = await injections.fileService.uploadFile(payload);
      actions.uploadSuccess(res);
    } catch (error) {
      actions.uploadError({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured trying to upload file.',
        asNotification: true,
      });
      return error;
    }
  }),
  fetchUploads: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fileActionStart();
      const { fileService } = injections;
      const res = await fileService.fetchUploads();
      actions.setUploads(res);
    } catch (error) {
      actions.uploadError({
        error,
        message: 'An error occured, unable to fetch files.',
        httpCode: parseStatusCode(error),
        asNotification: false,
      });
      return error;
    }
  }),
  uploadError: action((state, error) => {
    console.log('Error uploading a file', error);
  }),
  deleteUploadError: action((state, error) => {
    console.log('Error deleting file', error);
  }),
  setUploads: action((state, payload) => {
    state.fileUploads = payload;
  }),
  uploadSuccess: action((store, payload) => {
    console.log(payload);
  }),
  retrieveFile: thunk(async (actions, payload, { injections, getState }) => {
    try {
      const { fileService } = injections;
      const file = await fileService.retrieveFile(payload.s3Location);
      const blobData = new Blob([file]);
      FileSaver.saveAs(blobData, payload.fileName);
    } catch (err) {
      console.log(err);
    }
  }),
  deleteFile: thunk(async (actions, payload, { injections }) => {
    try {
      const { fileService } = injections;
      const deleteFileId = await fileService.deleteFile(payload);
      actions.removeFileFromlist(deleteFileId);
    } catch (error) {
      actions.deleteUploadError({
        error,
        message: 'Unable to delete file',
        httpCode: parseStatusCode(error),
        asNotification: true,
      });
    }
  }),
  setDeleteFile: action((state, payload) => {
    state.selectedDeleteFile = payload;
  }),
  removeFileFromlist: action((state, payload) => {
    state.fileUploads = state.fileUploads.filter((val: any) => {
      if (val.s3Location !== payload) {
        return val;
      }
      return null;
    });
  }),
};
